/**
 * Confirm Dialogs
 * 
 * Dialog ยืนยันการดำเนินการแบบ reusable
 * - ConfirmDialog: Generic confirm dialog
 * - SaveConfirmDialog: For save operations with changes list
 * - CreateConfirmDialog: For create operations with summary
 */

import { useState } from 'react'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Loader2, AlertTriangle, Info, CheckCircle, XCircle, Save, PlusCircle, LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

// ============================================
// Types
// ============================================

export type ConfirmVariant = 'default' | 'warning' | 'danger' | 'success' | 'info'

export interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  details?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  variant?: ConfirmVariant
  icon?: LucideIcon
  onConfirm: () => Promise<void> | void
}

// ============================================
// Config
// ============================================

const variantConfig: Record<ConfirmVariant, {
  icon: LucideIcon
  iconClass: string
  confirmClass: string
}> = {
  default: {
    icon: Info,
    iconClass: 'text-blue-500',
    confirmClass: 'bg-primary hover:bg-primary/90',
  },
  warning: {
    icon: AlertTriangle,
    iconClass: 'text-amber-500',
    confirmClass: 'bg-amber-600 hover:bg-amber-700',
  },
  danger: {
    icon: XCircle,
    iconClass: 'text-red-500',
    confirmClass: 'bg-red-600 hover:bg-red-700',
  },
  success: {
    icon: CheckCircle,
    iconClass: 'text-emerald-500',
    confirmClass: 'bg-emerald-600 hover:bg-emerald-700',
  },
  info: {
    icon: Info,
    iconClass: 'text-blue-500',
    confirmClass: 'bg-blue-600 hover:bg-blue-700',
  },
}

// ============================================
// Main Component
// ============================================

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  details,
  confirmLabel = 'ยืนยัน',
  cancelLabel = 'ยกเลิก',
  variant = 'warning',
  icon: CustomIcon,
  onConfirm,
}: ConfirmDialogProps) {
  const [isLoading, setIsLoading] = useState(false)
  const config = variantConfig[variant]
  const Icon = CustomIcon || config.icon

  const handleConfirm = async () => {
    setIsLoading(true)
    try {
      await onConfirm()
      onOpenChange(false)
    } catch (error) {
      console.error('Action failed:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Icon className={cn("w-5 h-5", config.iconClass)} />
            {title}
          </AlertDialogTitle>
          {(description || details) && (
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                {description && <p>{description}</p>}
                {details && (
                  <div className="p-3 rounded-lg bg-muted text-sm text-foreground">
                    {details}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>{cancelLabel}</AlertDialogCancel>
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            className={cn(config.confirmClass)}
          >
            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

// ============================================
// SaveConfirmDialog
// ============================================

interface SaveConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => Promise<void> | void
  isLoading?: boolean
  itemName?: string
  title?: string
  description?: string
  changes?: Array<{ field: string; oldValue?: string; newValue?: string }>
}

export function SaveConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  isLoading = false,
  itemName = 'ข้อมูล',
  title,
  description,
  changes = [],
}: SaveConfirmDialogProps) {
  const handleConfirm = async () => {
    try {
      await onConfirm()
    } catch (error) {
      console.error('Save failed:', error)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Save className="w-5 h-5 text-blue-500" />
            {title || `ยืนยันบันทึก${itemName}`}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>{description || `คุณต้องการบันทึกการเปลี่ยนแปลง${itemName}หรือไม่?`}</p>
              {changes.length > 0 && (
                <div className="p-3 rounded-lg bg-muted text-sm space-y-1">
                  <p className="font-medium text-foreground mb-2">การเปลี่ยนแปลง:</p>
                  {changes.map((change, i) => (
                    <div key={i} className="text-muted-foreground">
                      • {change.field}
                      {change.oldValue && change.newValue && (
                        <span>: {change.oldValue} → {change.newValue}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>ยกเลิก</AlertDialogCancel>
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            บันทึก
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

// ============================================
// CreateConfirmDialog
// ============================================

interface CreateConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => Promise<void> | void
  isLoading?: boolean
  itemName?: string
  title?: string
  description?: string
  summary?: React.ReactNode
}

export function CreateConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  isLoading = false,
  itemName = 'รายการ',
  title,
  description,
  summary,
}: CreateConfirmDialogProps) {
  const handleConfirm = async () => {
    try {
      await onConfirm()
    } catch (error) {
      console.error('Create failed:', error)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <PlusCircle className="w-5 h-5 text-emerald-500" />
            {title || `ยืนยันสร้าง${itemName}`}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>{description || `คุณต้องการสร้าง${itemName}ใหม่หรือไม่?`}</p>
              {summary && (
                <div className="p-3 rounded-lg bg-muted text-sm text-foreground">
                  {summary}
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>ยกเลิก</AlertDialogCancel>
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            สร้าง
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
